import { useCallback, useEffect, useState } from 'react';

interface UseCountDownOptions {
  initialSeconds: number;
  autoStart?: boolean;
}

interface UseCountDownReturn {
  countdown: number;
  isExpired: boolean;
  isRunning: boolean;
  formattedTime: string;
  start: () => void;
  pause: () => void;
  reset: () => void;
  resetAndStop: () => void;
}

export const useCountDown = ({
  initialSeconds,
  autoStart = false,
}: UseCountDownOptions): UseCountDownReturn => {
  const [countdown, setCountdown] = useState(initialSeconds);
  const [isRunning, setIsRunning] = useState(autoStart);

  const formatTime = useCallback((seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  }, []);

  const start = useCallback(() => {
    setIsRunning(true);
  }, []);

  const pause = useCallback(() => {
    setIsRunning(false);
  }, []);

  const resetAndStop = useCallback(() => {
    setCountdown(initialSeconds);
    setIsRunning(false);
  }, [initialSeconds]);

  const reset = useCallback(() => {
    setCountdown(initialSeconds);
    setIsRunning(true);
  }, [initialSeconds]);

  useEffect(() => {
    if (!isRunning || countdown <= 0) return;

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          setIsRunning(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isRunning, countdown]);

  return {
    countdown,
    isExpired: countdown <= 0,
    isRunning,
    formattedTime: formatTime(countdown),
    start,
    pause,
    reset,
    resetAndStop,
  };
};
